import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/user/_layout/attendance/adjustments")({
  component: UserAttendanceAdjustments,
});

import { useEffect, useMemo, useState } from "react";
import { FilePen } from "lucide-react";
import { toast } from "sonner";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";

import useFetchAdjustmentRequests from "@/hooks/api/attendance-adjustments/useFetchAdjustmentRequests";
import useCancelAttendanceAdjustment from "@/hooks/api/attendance-adjustments/useCancelAttendanceAdjustment";
import useCancelBulkAdjustmentRequest from "@/hooks/api/attendance-adjustments/useCancelBulkAdjustmentRequest";

import { AdjustmentRequestTable } from "@/components/crud/table/AdjustmentRequestTable";
import { AttendanceAdjustmentDetailSheet } from "@/components/crud/sheets/AttendanceAdjustmentDetailSheet";
import { TimesheetBulkAdjustmentDetailSheet } from "@/components/crud/sheets/TimesheetBulkAdjustmentDetailSheet";
import { RequestAdjustmentDialog } from "@/components/crud/modals/RequestAdjustmentDialog";

import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import type {
  AttendanceAdjustment,
  AttendanceAdjustmentStatus,
  CombinedAdjustmentRequest,
  TimesheetBulkAdjustment,
} from "@/types/attendance-adjustment";

function UserAttendanceAdjustments() {
  const [statusFilter, setStatusFilter] = useState<
    AttendanceAdjustmentStatus | "clear" | undefined
  >();
  const [showFilters, setShowFilters] = useState(false);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [selectedAdjustment, setSelectedAdjustment] =
    useState<CombinedAdjustmentRequest | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] =
    useState<CombinedAdjustmentRequest | null>(null);

  // `false` = the employee endpoint, which scopes to the caller server-side.
  // Search and user_id are deliberately not passed — that endpoint ignores them.
  const adjustmentsQuery = useFetchAdjustmentRequests(
    {
      page,
      page_size: pageSize,
      status: statusFilter === "clear" ? undefined : statusFilter,
    },
    false,
  );
  const adjustments = adjustmentsQuery.data?.results ?? [];
  const count = adjustmentsQuery.data?.count ?? 0;

  const cancelAttendanceAdjustment = useCancelAttendanceAdjustment();
  const cancelBulkAdjustmentRequest = useCancelBulkAdjustmentRequest();
  const isCancelling =
    cancelAttendanceAdjustment.isPending || cancelBulkAdjustmentRequest.isPending;

  const isLoading = adjustmentsQuery.isPending || isCancelling;
  const isFetching = adjustmentsQuery.isFetching || isCancelling;

  // Back to page 1 whenever the filter changes.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, setPage]);

  // The combined feed hardcodes next/previous to null, so paging is computed
  // from `count` rather than followed by link.
  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize],
  );

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleViewDetails = (adjustment: CombinedAdjustmentRequest) => {
    setSelectedAdjustment(adjustment);
    setIsDetailSheetOpen(true);
  };

  // Cancel routes to whichever mutation matches the row's type — a full-day
  // request cancels by id alone, a single-action one round-trips its fields.
  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      if (cancelTarget.request_type === "FULL_DAY") {
        await cancelBulkAdjustmentRequest.mutateAsync(cancelTarget.id);
      } else {
        await cancelAttendanceAdjustment.mutateAsync({
          id: cancelTarget.id,
          payload: {
            timesheet_id: cancelTarget.timesheet?.id,
            action: cancelTarget.action,
            time: cancelTarget.time,
            remarks: cancelTarget.remarks,
          },
        });
      }
      toast.success("Adjustment request cancelled");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    } finally {
      setCancelTarget(null);
    }
  };

  // Narrow the discriminated union explicitly so each detail sheet gets its
  // own concrete type.
  let singleAdjustment: AttendanceAdjustment | null = null;
  let bulkAdjustment: TimesheetBulkAdjustment | null = null;
  if (selectedAdjustment?.request_type === "FULL_DAY") {
    bulkAdjustment = selectedAdjustment;
  } else if (selectedAdjustment) {
    singleAdjustment = selectedAdjustment;
  }

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="Attendance Adjustments"
          description="Track the attendance adjustments you have requested."
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={() => setIsCreateDialogOpen(true)}
          addLabel="Request Adjustment"
          ButtonIcon={FilePen}
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => {
            setStatusFilter(undefined);
            setPage(1);
          }}
          isLoading={isFetching}
        >
          <SelectFilter
            placeholder="Status"
            options={[
              { label: "Pending", value: "PENDING" },
              { label: "Approved", value: "APPROVED" },
              { label: "Rejected", value: "REJECTED" },
              { label: "Cancelled", value: "CANCELLED" },
              { label: "All", value: "clear" },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <AdjustmentRequestTable
          adjustments={adjustments}
          isLoading={isLoading}
          onViewDetails={handleViewDetails}
          onCancelRequest={setCancelTarget}
          hideUserColumn
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        {/* showActions is left at its default false — an employee reviews
            nothing, so both sheets render read-only. */}
        {bulkAdjustment ? (
          <TimesheetBulkAdjustmentDetailSheet
            isOpen={isDetailSheetOpen}
            onClose={() => setIsDetailSheetOpen(false)}
            adjustment={bulkAdjustment}
            isLoading={isCancelling}
          />
        ) : (
          <AttendanceAdjustmentDetailSheet
            isOpen={isDetailSheetOpen}
            onClose={() => setIsDetailSheetOpen(false)}
            adjustment={singleAdjustment}
            isLoading={isCancelling}
          />
        )}

        <RequestAdjustmentDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          timesheet={null}
          onSuccess={() => adjustmentsQuery.refetch()}
          allowDateSelection
        />

        <AlertDialog
          open={cancelTarget != null}
          onOpenChange={(open) => !open && setCancelTarget(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this request?</AlertDialogTitle>
              <AlertDialogDescription>
                This withdraws the adjustment request. It cannot be undone —
                you would need to submit a new request instead.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isCancelling}>
                Keep request
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleCancelConfirm}
                disabled={isCancelling}
              >
                Cancel request
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SidebarInset>
  );
}
